import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { BootstrapUI, bootstrapUI } from '@/uiControls/base/bootstrapui';
import { BaseUITools } from '@/uiControls/base/baseUITools';

// 导入翻译文件
import enTranslation from '../locales/en/translation.json';
import zhTranslation from '../locales/zh/translation.json';
import jaTranslation from '../locales/ja/translation.json';
import { globalManager } from '@/utils/globalConfig';
import { LangToolItem } from '@/uiControls/lang-tools';

const resources = {
  en: {
    translation: enTranslation
  },
  zh: {
    translation: zhTranslation
  },
  ja: {
    translation: jaTranslation
  }
};

class i18NextClass {
  private ui: BootstrapUI;
  private initPromise: Promise<void>;
  
  /**
   * 检测并获取初始语言
   * 与 public/scripts/i18n.lang.js 使用相同的逻辑
   */
  private detectInitialLanguage(): string {
    // 1. 优先从 localStorage 读取用户语言偏好（使用相同的 key）
    const savedLang = localStorage.getItem('preferred-language');
    if (savedLang && (savedLang === 'en' || savedLang === 'zh')) {
      return savedLang;
    }
    
    // 2. 如果无效，根据浏览器语言自动检测
    const browserLang = navigator.language || (navigator as any).userLanguage;
    if (browserLang && browserLang.startsWith('zh')) {
      return 'zh';
    }
    
    // 3. 默认返回英文
    return 'en';
  }

  constructor() {
    this.ui = bootstrapUI;
    
    // 检测初始语言
    const initialLang = this.detectInitialLanguage();
    
    // 保存初始化 Promise，以便后续等待
    this.initPromise = i18next
      .use(LanguageDetector)
      .init({
        lng: initialLang,
        resources,
        fallbackLng: 'en',
        debug: false,
        interpolation: {
          escapeValue: false, // React 已经转义了
        },
        detection: {
          // 自定义检测顺序：优先使用我们检测到的语言
          // 但保留 LanguageDetector 的其他功能
          order: ['querystring', 'cookie', 'localStorage', 'navigator', 'htmlTag'],
          caches: ['localStorage'],
          // 注意：i18next 默认使用 'i18nextLng' 作为 localStorage key
          // 但我们会在 changeLanguage 中同步到 'preferred-language'
        }
      }).then(() => {
        // 初始化完成后，确保语言设置正确并同步到 localStorage
        const detectedLang = this.detectInitialLanguage();
        const currentLang = i18next.language?.toLowerCase();
        
        // 如果当前语言不是 'en' 或 'zh'，或者与检测到的语言不一致，则切换
        if ((currentLang !== 'en' && currentLang !== 'zh') || currentLang !== detectedLang) {
          return i18next.changeLanguage(detectedLang).then(() => {
            // 确保保存到 localStorage
            localStorage.setItem('preferred-language', detectedLang);
          });
        } else {
          // 即使语言正确，也确保 localStorage 中有保存
          localStorage.setItem('preferred-language', currentLang);
        }
      });
  }
  async setupLanguageSwitcher(parentdom: BaseUITools, types: LangToolItem[]) {
    // 等待 i18next 初始化完成
    await this.initPromise;

    if (types.length == 0) {
      console.log("没有指定语言类型")
    }

    // 确保当前语言是有效的（只支持 'en' 和 'zh'）
    let currentLang = i18next.language?.toLowerCase();
    if (currentLang !== 'en' && currentLang !== 'zh') {
      // 如果语言无效，使用检测到的语言
      const detectedLang = this.detectInitialLanguage();
      await i18next.changeLanguage(detectedLang);
      currentLang = detectedLang;
    }
    
    console.log('setupLanguageSwitcher start', i18next.language);

    types.forEach((item) => {
      const domElement = document.createElement('button');
      domElement.id = `lang-${item.id}`;
      domElement.title = item.name;
      domElement.setAttribute('data-i18n-title', item.i18nTitle);
      domElement.classList.add('dropdown-tab-button', 'btn', 'btn-outline-primary', 'align-items-center', 'justify-content-center', 'w-100');
      // domElement.innerHTML = `<i class="icon iconfont icon-${item.iconName} icon-dropdown-tab-button icon-sm"></i>`;
      domElement.textContent = item.label;
      parentdom.appendChild(`lang-${item.id}`, domElement);

      // 创建时根据当前语言设置按钮样式
      if (item.id === currentLang) {
        domElement.classList.add('btn-current-item');
      } else {
        domElement.classList.add('btn-other-item');
      }

      // 语言切换按钮事件
      domElement.addEventListener('click', () => {
        this.changeLanguage(item.id);
        Object.keys(parentdom.getChildren()).forEach((childid) => {
          const child = parentdom.getChildren()[childid];
          child.classList.remove('btn-current-item');
          child.classList.add('btn-other-item');
        });
        domElement.classList.remove('btn-other-item');
        domElement.classList.add('btn-current-item');
      });
    })

    // 监听语言变化事件
    i18next.on('languageChanged', () => {
      this.updatePageContent();
    });

    // 延迟执行 updatePageContent，确保所有 DOM 元素都已创建
    // 使用 setTimeout 确保在下一个事件循环中执行，此时 RightPanelTools 的 DOM 应该已经创建完成
    setTimeout(() => {
      this.updatePageContent();
    }, 0);
  }

  changeLanguage(lng: string) {
    // 只支持 'en' 和 'zh'，与 i18n.lang.js 保持一致
    if (lng !== 'en' && lng !== 'zh') {
      console.warn(`Invalid language: ${lng}. Supported languages: 'en', 'zh'`);
      return;
    }
    
    i18next.changeLanguage(lng).then(() => {
      // 同步保存到 localStorage（使用与 i18n.lang.js 相同的 key）
      localStorage.setItem('preferred-language', lng);
      this.updatePageContent();
    });
  }

  updatePageContent() {
    // 检查 i18next 是否已初始化
    if (!i18next.isInitialized) {
      console.warn('i18next is not initialized yet, skipping updatePageContent');
      return;
    }

    // 更新所有带有 data-i18n 属性的元素
    const elements = document.querySelectorAll('[data-i18n]');
    elements.forEach(element => {
      const key = element.getAttribute('data-i18n');
      if (!key) return;

      const text = i18next.t(key);
      // 如果翻译失败（返回 key 本身），跳过更新，避免覆盖原有内容
      if (text === key && !i18next.exists(key)) {
        console.warn(`Translation key "${key}" not found, skipping update`);
        return;
      }

      if (element instanceof HTMLInputElement && element.type === 'range') {
        // 对于滑块，只更新标签
        const label = element.previousElementSibling;
        if (label && label.hasAttribute('data-i18n')) {
          const labelKey = label.getAttribute('data-i18n');
          if (!labelKey) return;
          const labelText = i18next.t(labelKey);
          if (labelText !== labelKey || i18next.exists(labelKey)) {
            label.textContent = labelText;
          }
        }
      } else if (element.tagName === 'INPUT' && element.hasAttribute('data-i18n-placeholder')) {
        // 如果元素同时有 data-i18n 和 data-i18n-placeholder，跳过这里，由后面的 placeholder 处理逻辑处理
        // 这样可以避免重复处理
        return;
      } else {
        // 只更新文本内容，保留其他属性
        element.textContent = text;
      }
    });

    // 更新所有带有 data-i18n-placeholder 属性的 input 元素的 placeholder
    const placeholderElements = document.querySelectorAll('[data-i18n-placeholder]');
    placeholderElements.forEach(element => {
      const key = element.getAttribute('data-i18n-placeholder');
      if (!key) return;

      const text = i18next.t(key);
      if (text !== key && i18next.exists(key)) {
        if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
          (element as HTMLInputElement | HTMLTextAreaElement).placeholder = text;
        }
      }
    });

    // 更新所有带有 data-i18n-title 属性的元素的 title
    const titleElements = document.querySelectorAll('[data-i18n-title]');
    titleElements.forEach(element => {
      const key = element.getAttribute('data-i18n-title');
      if (!key) return;

      const text = i18next.t(key);
      if (text !== key && i18next.exists(key)) {
        // 对于部分自定义提示场景，避免浏览器原生 title tooltip（无法精确控色）
        // 改为 data-tooltip + CSS tooltip 渲染。
        const el = element as HTMLElement;
        if (
          el.classList.contains('marble-import-info-tooltip')
        ) {
          el.setAttribute('data-tooltip', text);
          el.setAttribute('title', '');
        } else {
          el.setAttribute('title', text);
        }
      }
    });

    // 更新语言按钮状态
    this.updateLanguageButtons();
  }

  updateLanguageButtons() {
    const currentLang = i18next.language;
    const langButtons = ['lang-en', 'lang-zh', 'lang-ja'];

    langButtons.forEach(btnId => {
      const btn = document.getElementById(btnId);
      if (btn) {
        if (btnId === `lang-${currentLang}`) {
          btn.classList.remove('btn-other-item');
          btn.classList.add('btn-current-item');
        } else {
          btn.classList.remove('btn-current-item');
          btn.classList.add('btn-other-item');
        }
      }
    });
  }

}
export default i18NextClass;
